import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import alert from '../utils/alertService';
import { createModalHandlers, createButtonHandler, preventBodyScroll } from '../utils/modalUtils';
import './Events.css';

const Events = () => {
  usePageTitle('Events');
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    points: 10,
    poster: null,
  });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { user, isFounder } = useAuth();
  
  // Detect mobile device for performance optimizations
  const isMobile = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           (window.innerWidth <= 768);
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/events');
      
      // Ensure response.data is always an array
      let eventsData = response.data;
      
      // Check if response is HTML (routing issue)
      if (typeof eventsData === 'string' && eventsData.trim().startsWith('<!')) {
        eventsData = [];
      } else if (!Array.isArray(eventsData)) {
        // If it's an object with a data property, use that
        if (eventsData && Array.isArray(eventsData.data)) {
          eventsData = eventsData.data;
        } else {
          eventsData = [];
        }
      }
      
      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Prevent body scroll when modal is open (enhanced for mobile)
  useEffect(() => {
    if (showEventModal || showCreateModal) {
      preventBodyScroll(true);
    } else {
      preventBodyScroll(false);
    }
    return () => {
      preventBodyScroll(false);
    };
  }, [showEventModal, showCreateModal]);

  // Check if current user is registered for an event
  const isUserRegistered = useCallback((event) => {
    if (!user || !event.registrations) return false;
    return event.registrations.some(reg => reg.user?.id === user.id);
  }, [user]);

  // Get user's registration status
  const getUserRegistration = useCallback((event) => {
    if (!user || !event.registrations) return null;
    return event.registrations.find(reg => reg.user?.id === user.id);
  }, [user]);

  const handleRegister = async (eventId, status) => {
    try {
      await api.post(`/events/${eventId}/register`, { status });
      fetchEvents();
      // Refresh selected event if modal is open
      if (showEventModal && selectedEvent?.id === eventId) {
        fetchEventDetails(eventId);
      }
    } catch (error) {
      alert.error(error.response?.data?.message || 'Error registering for event');
    }
  };

  const handleEventClick = async (eventId) => {
    setShowEventModal(true);
    setEventLoading(true);
    await fetchEventDetails(eventId);
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}`);
      setSelectedEvent(response.data);
    } catch (error) {
      console.error('Error fetching event details:', error);
      alert.error('Error loading event details');
      setShowEventModal(false);
    } finally {
      setEventLoading(false);
    }
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);

    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('event_date', formData.event_date);
      data.append('event_time', formData.event_time);
      data.append('points', formData.points || 10);
      if (formData.poster) {
        data.append('poster', formData.poster);
      }

      await api.post('/events', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert.success('Event created successfully!');
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        points: 10,
        poster: null,
      });
      fetchEvents();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Error creating event');
      console.error('Error creating event:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleFormChange = (e) => {
    if (e.target.type === 'file') {
      setFormData({ ...formData, poster: e.target.files[0] });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    
    // Format event_date from database format (YYYY-MM-DD) to input format
    let formattedDate = '';
    if (event.event_date) {
      const date = new Date(event.event_date);
      if (!isNaN(date.getTime())) {
        // Format as YYYY-MM-DD for input type="date"
        formattedDate = date.toISOString().split('T')[0];
      }
    }
    
    // Format event_time (remove seconds if present)
    let formattedTime = '';
    if (event.event_time) {
      formattedTime = event.event_time.substring(0, 5); // HH:MM format
    }
    
    setFormData({
      title: event.title || '',
      description: event.description || '',
      event_date: formattedDate,
      event_time: formattedTime,
      points: event.points || 10,
      poster: null,
    });
    setShowEditModal(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    setCreateError('');
    setUpdating(true);

    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('event_date', formData.event_date);
      data.append('event_time', formData.event_time);
      data.append('points', formData.points || 10);
      if (formData.poster) {
        data.append('poster', formData.poster);
      }
      // Use _method for PUT request
      data.append('_method', 'PUT');

      await api.post(`/events/${editingEvent.id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert.success('Event updated successfully!');
      setShowEditModal(false);
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        points: 10,
        poster: null,
      });
      fetchEvents();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Error updating event');
      console.error('Error updating event:', err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="events-container">
      <div className="page-header">
        <h1>Events</h1>
        {isFounder && (
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            ➕ Add New Event
          </button>
        )}
      </div>
      {!Array.isArray(events) || events.length === 0 ? (
        <div className="no-data">
          <p>No events available at the moment.</p>
        </div>
      ) : (
        <div className={`events-grid ${isMobile ? 'mobile-optimized' : ''}`}>
          {events.map((event) => {
            // Default images with space/alien theme from Unsplash
            const defaultImages = [
              'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=400&h=200&fit=crop&q=80&auto=format', // Space
              'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=400&h=200&fit=crop&q=80&auto=format', // Galaxy
              'https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=400&h=200&fit=crop&q=80&auto=format', // Stars
            ];
            // Use different default image based on event ID for variety
            const defaultImageIndex = (event.id - 1) % defaultImages.length;
            const defaultImage = defaultImages[defaultImageIndex];
            
            const posterUrl = event.poster_url || (event.poster 
              ? `https://aliensteam.com/storage/${event.poster}?t=${Date.now()}` 
              : defaultImage);
            
            return (
            <div key={event.id} className="event-card" onClick={() => handleEventClick(event.id)}>
              <div className="event-poster-wrapper">
                <img
                  key={`event-poster-${event.id}-${event.updated_at || Date.now()}`}
                  src={posterUrl}
                  alt={event.title}
                  className="event-poster"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    // Fallback to another default image if first fails
                    if (e.target.src !== defaultImage) {
                      e.target.src = defaultImage;
                    } else {
                      // Ultimate fallback - gradient background with text
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="event-poster-placeholder" style={{ display: 'none' }}>
                  <div className="placeholder-icon">👽</div>
                  <div className="placeholder-text">{event.title}</div>
                </div>
              </div>
            <div className="event-content">
              <h3>{event.title}</h3>
              <p className="event-description">{event.description}</p>
              <div className="event-details">
                <span>📅 {new Date(event.event_date).toLocaleDateString('en-US')}</span>
                <span>🕐 {event.event_time}</span>
              </div>
              <div className="event-footer">
                {isFounder && (
                  <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn-secondary"
                      onClick={() => handleEditEvent(event)}
                      style={{ marginRight: '10px' }}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                )}
                {user && !isFounder && !isUserRegistered(event) && (
                  <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn-success"
                      onClick={() => handleRegister(event.id, 'coming')}
                    >
                      I'm Coming
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleRegister(event.id, 'not_coming')}
                    >
                      Not Coming
                    </button>
                  </div>
                )}
                {user && !isFounder && isUserRegistered(event) && (
                  <div 
                    className={`registration-status ${getUserRegistration(event)?.status === 'coming' ? 'coming-status' : 'not-coming-status'}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="status-message">
                      {getUserRegistration(event)?.status === 'coming' ? '✅' : '❌'} You are registered as {getUserRegistration(event)?.status === 'coming' ? 'Coming' : 'Not Coming'}
                    </span>
                  </div>
                )}
                {event.registrations && (
                  <div className="registrations-count">
                    Registrations: {event.registrations.length}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
          })}
        </div>
          )}
      

      {/* Event Details Modal */}
      {showEventModal && (
        <div 
          className="modal-overlay" 
          {...createModalHandlers(closeEventModal)}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close" 
              {...createButtonHandler(closeEventModal)}
            >
              ✕
            </button>
            {eventLoading ? (
              <div className="modal-body">
                <div className="loading">
                  <div className="loading-text">Loading event details...</div>
                </div>
              </div>
            ) : selectedEvent ? (
              <>
                <div className="modal-header">
                  <div className="modal-poster-wrapper">
                    {(() => {
                      const defaultImages = [
                        'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=800&h=400&fit=crop&q=80&auto=format',
                        'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&h=400&fit=crop&q=80&auto=format',
                        'https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=800&h=400&fit=crop&q=80&auto=format',
                      ];
                      const defaultImageIndex = (selectedEvent.id - 1) % defaultImages.length;
                      const defaultImage = defaultImages[defaultImageIndex];
                      const posterUrl = selectedEvent.poster_url || (selectedEvent.poster 
                        ? `https://aliensteam.com/storage/${selectedEvent.poster}?t=${Date.now()}` 
                        : defaultImage);
                      return (
                        <img
                          key={`modal-poster-${selectedEvent.id}-${selectedEvent.updated_at || Date.now()}`}
                          src={posterUrl}
                          alt={selectedEvent.title}
                          className="modal-poster"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            if (e.target.src !== defaultImage) {
                              e.target.src = defaultImage;
                            }
                          }}
                        />
                      );
                    })()}
                  </div>
                  <h2>{selectedEvent.title}</h2>
                </div>

                <div className="modal-body">
                  {selectedEvent.description && (
                    <div className="modal-section">
                      <h3>
                        <span className="section-icon">📄</span>
                        Description
                      </h3>
                      <p className="description-text">{selectedEvent.description}</p>
                    </div>
                  )}

                  <div className="modal-section">
                    <h3>
                      <span className="section-icon">📅</span>
                      Event Details
                    </h3>
                    <div className="modal-details-grid">
                      <div className="detail-item">
                        <span className="detail-icon">📅</span>
                        <div className="detail-content">
                          <span className="detail-label">Date</span>
                          <span className="detail-value">
                            {new Date(selectedEvent.event_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      
                      <div className="detail-item">
                        <span className="detail-icon">🕐</span>
                        <div className="detail-content">
                          <span className="detail-label">Time</span>
                          <span className="detail-value">{selectedEvent.event_time || 'Not specified'}</span>
                        </div>
                      </div>
                      
                      {selectedEvent.creator && (
                        <div className="detail-item">
                          <span className="detail-icon">👤</span>
                          <div className="detail-content">
                            <span className="detail-label">Created By</span>
                            <span className="detail-value">{selectedEvent.creator.name || 'Unknown'}</span>
                          </div>
                        </div>
                      )}

                      {isFounder && selectedEvent.registrations && (() => {
                        const coming = selectedEvent.registrations.filter(reg => reg.status === 'coming').length;
                        const notComing = selectedEvent.registrations.filter(reg => reg.status === 'not_coming').length;
                        return (
                          <>
                            <div className="detail-item coming-count">
                              <span className="detail-icon">✅</span>
                              <div className="detail-content">
                                <span className="detail-label">Coming</span>
                                <span className="detail-value coming-value">
                                  {coming} {coming === 1 ? 'person' : 'people'}
                                </span>
                              </div>
                            </div>
                            <div className="detail-item not-coming-count">
                              <span className="detail-icon">❌</span>
                              <div className="detail-content">
                                <span className="detail-label">Not Coming</span>
                                <span className="detail-value not-coming-value">
                                  {notComing} {notComing === 1 ? 'person' : 'people'}
                                </span>
                              </div>
                            </div>
                            <div className="detail-item">
                              <span className="detail-icon">👥</span>
                              <div className="detail-content">
                                <span className="detail-label">Total</span>
                                <span className="detail-value">
                                  {selectedEvent.registrations.length} {selectedEvent.registrations.length === 1 ? 'person' : 'people'}
                                </span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {isFounder && selectedEvent.registrations && selectedEvent.registrations.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <span className="section-icon">👥</span>
                        Registered Users ({selectedEvent.registrations.length})
                      </h3>
                      <div className="registrations-list">
                        {selectedEvent.registrations.map((reg, index) => (
                          <div key={reg.id || index} className="registration-item">
                            <div className="registration-user-info">
                              <span className="user-avatar">
                                {reg.user?.name ? reg.user.name.charAt(0).toUpperCase() : '?'}
                              </span>
                              <span className="user-name">{reg.user?.name || 'Unknown User'}</span>
                            </div>
                            <span className={`status-badge ${reg.status === 'coming' ? 'coming' : 'not-coming'}`}>
                              {reg.status === 'coming' ? '✅ Coming' : '❌ Not Coming'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isFounder && (!selectedEvent.registrations || selectedEvent.registrations.length === 0) && (
                    <div className="modal-section">
                      <div className="empty-state">
                        <span className="empty-icon">👥</span>
                        <p>No registrations yet. Be the first to register!</p>
                      </div>
                    </div>
                  )}

                  {/* Registration Actions/Status */}
                  {user && !isFounder && (() => {
                    const isRegistered = selectedEvent.registrations?.some(reg => reg.user?.id === user.id);
                    const registration = selectedEvent.registrations?.find(reg => reg.user?.id === user.id);
                    
                    if (!isRegistered) {
                      return (
                        <div className="modal-section">
                          <div className="modal-actions">
                            <button
                              className="btn-success"
                              onClick={() => handleRegister(selectedEvent.id, 'coming')}
                            >
                              ✅ I'm Coming
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => handleRegister(selectedEvent.id, 'not_coming')}
                            >
                              ❌ Not Coming
                            </button>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="modal-section">
                          <div className="modal-registration-status">
                            <div className="status-card">
                              <span className="status-icon">
                                {registration?.status === 'coming' ? '✅' : '❌'}
                              </span>
                              <div className="status-info">
                                <h4>You are registered</h4>
                                <p>Status: <strong>{registration?.status === 'coming' ? 'Coming' : 'Not Coming'}</strong></p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })()}
                  {!user && (
                    <div className="modal-section">
                      <div className="modal-login-prompt">
                        <p>Please log in to register for this event</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div 
          className="modal-overlay" 
          {...createModalHandlers(() => setShowEditModal(false))}
        >
          <div 
            className="modal-content create-event-modal" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close" 
              {...createButtonHandler(() => setShowEditModal(false))}
            >
              ✕
            </button>
            <div className="modal-header">
              <h2>✏️ Edit Event</h2>
            </div>
            {createError && <div className="error-message">{createError}</div>}
            <form onSubmit={handleUpdateEvent}>
              <div className="form-group">
                <label>📝 Event Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Enter event title"
                  required
                />
              </div>

              <div className="form-group">
                <label>📄 Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Enter event description"
                  rows="5"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>📅 Event Date</label>
                  <input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🕐 Event Time</label>
                  <input
                    type="time"
                    name="event_time"
                    value={formData.event_time}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>⭐ Points Reward</label>
                <input
                  type="number"
                  name="points"
                  value={formData.points}
                  onChange={handleFormChange}
                  placeholder="Points to give for attending"
                  min="0"
                  required
                />
                <small className="form-help">Points users will receive when they register as "Coming"</small>
              </div>

              <div className="form-group">
                <label>🖼️ Event Poster (Optional)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    name="poster"
                    accept="image/*"
                    onChange={handleFormChange}
                    className="file-input"
                    id="poster-upload-edit"
                  />
                  <label htmlFor="poster-upload-edit" className="file-upload-label">
                    {formData.poster ? formData.poster.name : 'Choose New Image (leave empty to keep current)'}
                  </label>
                </div>
                {editingEvent.poster && !formData.poster && (
                  <div className="preview-container">
                    <p>Current poster:</p>
                    <img
                      src={editingEvent.poster_url ? `${editingEvent.poster_url}?t=${Date.now()}` : (editingEvent.poster ? `https://aliensteam.com/storage/${editingEvent.poster}?t=${Date.now()}` : '')}
                      alt="Current poster"
                      className="poster-preview"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                {formData.poster && (
                  <div className="preview-container">
                    <p>New poster preview:</p>
                    <img
                      src={URL.createObjectURL(formData.poster)}
                      alt="Poster preview"
                      className="poster-preview"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingEvent(null);
                    setCreateError('');
                  }}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating}
                >
                  {updating ? 'Updating...' : '💾 Update Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div 
          className="modal-overlay" 
          {...createModalHandlers(() => setShowCreateModal(false))}
        >
          <div 
            className="modal-content create-event-modal" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close" 
              {...createButtonHandler(() => setShowCreateModal(false))}
            >
              ✕
            </button>
            <div className="modal-header">
              <h2>✨ Create New Event</h2>
            </div>
            {createError && <div className="error-message">{createError}</div>}
            <form onSubmit={handleCreateEvent}>
              <div className="form-group">
                <label>📝 Event Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Enter event title"
                  required
                />
              </div>

              <div className="form-group">
                <label>📄 Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Enter event description"
                  rows="5"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>📅 Event Date</label>
                  <input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleFormChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🕐 Event Time</label>
                  <input
                    type="time"
                    name="event_time"
                    value={formData.event_time}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>⭐ Points Reward</label>
                <input
                  type="number"
                  name="points"
                  value={formData.points}
                  onChange={handleFormChange}
                  placeholder="Points to give for attending"
                  min="0"
                  required
                />
                <small className="form-help">Points users will receive when they register as "Coming"</small>
              </div>

              <div className="form-group">
                <label>🖼️ Event Poster (Optional)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    name="poster"
                    accept="image/*"
                    onChange={handleFormChange}
                    className="file-input"
                    id="poster-upload"
                  />
                  <label htmlFor="poster-upload" className="file-upload-label">
                    {formData.poster ? formData.poster.name : 'Choose Image'}
                  </label>
                </div>
                {formData.poster && (
                  <div className="preview-container">
                    <img
                      src={URL.createObjectURL(formData.poster)}
                      alt="Poster preview"
                      className="poster-preview"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : '✨ Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;





